import * as React from "react";
import { Popover as AtlasPopover } from "@wellsky/atlas-ui-react";
import type { PopoverPlacement } from "@wellsky/atlas-ui-react";
import { PortalContext } from "../../widget/WidgetProvider";

const TRIGGER_MARKER = "data-cq-popover-trigger";
const CONTENT_MARKER = "data-cq-popover-content";

interface PopoverProps {
  /** Trigger element — should be a host element (e.g. <button>) so it can be ref'd. */
  trigger: React.ReactElement;
  /** Popover content. */
  children: React.ReactNode;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  placement?: PopoverPlacement;
  /** Class applied to the popover container. */
  className?: string;
  ariaLabel?: string;
}

/**
 * Thin adapter over atlas-ui-react's Popover that:
 *  - portals content into the widget's Shadow DOM (PortalContext), and
 *  - detects outside clicks via composedPath() instead of e.target.
 *
 * The shadow boundary retargets e.target to the host element, so atlas's
 * built-in outside-click closes the popover on inner clicks (same class of
 * bug worked around for the dropdown menu in NETSOL-11402). We disable the
 * built-in handler and detect outside clicks ourselves using composedPath,
 * which reports the true element path through the shadow root.
 */
export function Popover({
  trigger,
  children,
  open,
  onOpenChange,
  placement = "bottom-start",
  className,
  ariaLabel,
}: PopoverProps) {
  const portalContainer = React.useContext(PortalContext);

  // Atlas design tokens and the data-theme are scoped to the
  // `.mds-widget-container` element inside the shadow root (the widget build
  // rewrites Tailwind's `:root,:host` to that class). Portaling to the raw
  // shadow root would render the popover OUTSIDE that scope, so token-driven
  // colors (e.g. the Atlas Calendar's selected-day text) would not resolve.
  // Portal into the styled container instead so tokens + theme apply.
  const container = React.useMemo<HTMLElement | undefined>(() => {
    if (portalContainer instanceof ShadowRoot) {
      return (
        (portalContainer.querySelector(
          ".mds-widget-container",
        ) as HTMLElement | null) ?? undefined
      );
    }
    return (portalContainer as HTMLElement | null) ?? undefined;
  }, [portalContainer]);

  React.useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      const path = e.composedPath();
      const inside = path.some(
        (node) =>
          node instanceof Element &&
          (node.hasAttribute(TRIGGER_MARKER) ||
            node.hasAttribute(CONTENT_MARKER)),
      );
      if (!inside) onOpenChange(false);
    };
    document.addEventListener("mousedown", handler, true);
    return () => document.removeEventListener("mousedown", handler, true);
  }, [open, onOpenChange]);

  // Tag the trigger so composedPath can recognise it, and provide an onClick
  // toggle fallback (preserved if atlas does not wire the trigger itself).
  const triggerProps = trigger.props as {
    onClick?: (e: React.MouseEvent) => void;
  };
  const wrappedTrigger = React.cloneElement(
    trigger as React.ReactElement<Record<string, unknown>>,
    {
      [TRIGGER_MARKER]: "",
      onClick: (e: React.MouseEvent) => {
        triggerProps.onClick?.(e);
        onOpenChange(!open);
      },
    },
  );

  const content = (
    <div {...{ [CONTENT_MARKER]: "" }}>{children}</div>
  );

  return (
    <AtlasPopover
      open={open}
      onOpenChange={onOpenChange}
      placement={placement}
      content={content}
      container={container}
      className={className}
      aria-label={ariaLabel}
      closeOnOutsideClick={false}
      closeOnEscape
    >
      {wrappedTrigger}
    </AtlasPopover>
  );
}
